import type { ReduxSagaModel } from './types';
import Spotlight from '@/services/spotlight';
import { sgcall, sgselect } from '@/utils/reduxSaga';
import { logger } from '@/utils/logger';
import type { SearchResult } from '@/components/SpotlightSearch/types';

const ns = 'app.models.spotlight';

export interface State {
  items: SearchResult[];
  loadedForWorkspace: string | null;
  isLoading: boolean;
}

const initialState: State = {
  items: [],
  loadedForWorkspace: null,
  isLoading: false,
};

interface SaveItemsAction {
  type: 'saveItems';
  payload: Pick<State, 'items' | 'loadedForWorkspace'>;
}

interface SetLoadingAction {
  type: 'setLoading';
  payload: Pick<State, 'isLoading'>;
}

const SpotlightModel: ReduxSagaModel<
  State,
  {
    saveItems: SaveItemsAction;
    setLoading: SetLoadingAction;
  }
> = {
  namespace: 'spotlight',
  state: initialState,
  effects: {
    *fetchSearchableData({ payload }: { payload: { workspaceId: string } }, { put }) {
      const loadedFor = yield* sgselect((s) => s.spotlight.loadedForWorkspace);
      if (loadedFor === payload.workspaceId) return;

      logger.debug({ label: `${ns}.fetchSearchableData`, message: payload.workspaceId });

      yield put<SetLoadingAction>({ type: 'setLoading', payload: { isLoading: true } });

      try {
        const items = yield* sgcall(() => Spotlight.fetchAll(payload.workspaceId));
        yield put<SaveItemsAction>({
          type: 'saveItems',
          payload: { items, loadedForWorkspace: payload.workspaceId },
        });
      } catch (e) {
        logger.debug({ label: `${ns}.fetchSearchableData.error`, message: e });
      } finally {
        yield put<SetLoadingAction>({ type: 'setLoading', payload: { isLoading: false } });
      }
    },
  },
  reducers: {
    resetAll() {
      return { ...initialState };
    },
    saveItems(state, { payload }) {
      return { ...state, ...payload };
    },
    setLoading(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default SpotlightModel;
